/**
 * Хелперы графики эфира MLBB (1920×1080): сцены, горячие клавиши, слоты драфта, килл-фид.
 * Без зависимостей от Vue — покрыто тестами в tests/unit/mlbbOverlay.spec.js.
 */
import {campDraftHeroes, campPlayers, playerHero} from '@/common/mlbbBroadcast'

export const OVERLAY_WIDTH = 1920
export const OVERLAY_HEIGHT = 1080

export const SCENES = [
    {key: 'none', hotkey: '0', title: 'Без графики', hint: 'чистая картинка игры'},
    {key: 'intro', hotkey: '1', title: 'Заставка', hint: 'турнир и команды'},
    {key: 'draft', hotkey: '2', title: 'Драфт', hint: 'баны, пики, игроки'},
    {key: 'game', hotkey: '3', title: 'Игра', hint: 'счёт, таймер, объекты'},
    {key: 'post', hotkey: '4', title: 'Итоги', hint: 'победитель и статистика'},
]

const SCENE_KEYS = SCENES.map((s) => s.key)

export function isScene(key) {
    return SCENE_KEYS.includes(key)
}

export function sceneTitle(key) {
    return SCENES.find((s) => s.key === key)?.title || '—'
}

export function sceneByHotkey(key) {
    return SCENES.find((s) => s.hotkey === key)?.key || null
}

// Какой сцене соответствует состояние игры — только подсказка, сцену режиссёр выбирает сам
export function suggestedScene(battle) {
    switch (battle?.state) {
        case 'init':
            return 'intro'
        case 'ban':
        case 'pick':
        case 'adjust':
            return 'draft'
        case 'loading':
        case 'play':
            return 'game'
        case 'end':
            return 'post'
        default:
            return null
    }
}

// Горячие клавиши не должны срабатывать, пока режиссёр что-то печатает
export function isTypingTarget(el) {
    if (!el) return false
    const tag = (el.tagName || '').toLowerCase()
    return tag === 'input' || tag === 'textarea' || tag === 'select' || !!el.isContentEditable
}

// 27539 → «27.5K», 980 → «980»; ноль показываем нулём
export function formatGold(value) {
    const n = Number(value)
    if (!Number.isFinite(n)) return '0'
    const abs = Math.abs(n)
    if (abs >= 10000) return `${Math.round(n / 1000)}K`
    if (abs >= 1000) return `${(n / 1000).toFixed(1)}K`
    return String(Math.round(n))
}

export const BANS_PER_CAMP = 5

/**
 * Пять слотов банов стороны: {hero, current}. current — слот, который сейчас банят
 * (по флагу banning у игроков стороны в фазе банов).
 */
export function banSlots(battle, campNumber) {
    const bans = campDraftHeroes(battle, campNumber, 'ban').filter((b) => b.id).slice(0, BANS_PER_CAMP)
    const banning = battle?.state === 'ban' && campPlayers(battle, campNumber).some((p) => p.banning)
    return Array.from({length: BANS_PER_CAMP}, (_, i) => ({
        hero: bans[i] ? bans[i].hero : null,
        current: banning && i === bans.length,
    }))
}

// Карточки драфта: игрок, его пик и признак «сейчас пикает»
export function pickCards(battle, campNumber) {
    const picking = battle?.state === 'pick'
    return campPlayers(battle, campNumber).map((p) => ({
        player: p,
        hero: playerHero(p),
        current: picking && !!p.picking,
    }))
}

const FEED_TYPES = ['kill_hero', 'kill_boss', 'kill_tower', 'camp_ace']

/**
 * Последние события для килл-фида поверх игры: только свежие (за windowSec игрового времени),
 * не больше limit, новые снизу.
 */
export function killfeed(events, battle, {limit = 3, windowSec = 15} = {}) {
    const now = Number(battle?.game_time) || 0
    return (events || [])
        .filter((e) => FEED_TYPES.includes(e.event_type) && e.game_time <= now && e.game_time >= now - windowSec)
        .slice(-limit)
}

/* ---------- плавный таймер ---------- */

// game_time приходит пачками раз в несколько секунд — между тиками считаем время сами
export const CLOCK_TICK_MS = 250
// дольше этого без новых данных не убегаем вперёд: лучше замереть, чем показать неправду
export const CLOCK_MAX_EXTRAPOLATE_SEC = 8
// расхождение больше — новая игра, перемотка или реконнект: прыгаем сразу
export const CLOCK_SNAP_SEC = 10

/**
 * Оценка реального игрового времени: последнее значение с сервера плюс прошедшее с его прихода,
 * только пока идёт игра и нет паузы.
 * @param {{gameTime: number, at: number, running: boolean}} anchor
 */
export function clockTarget(anchor, now = Date.now()) {
    if (!anchor) return 0
    const base = Number(anchor.gameTime) || 0
    if (!anchor.running) return base
    const elapsed = Math.max(0, (now - anchor.at) / 1000)
    return base + Math.min(elapsed, CLOCK_MAX_EXTRAPOLATE_SEC)
}

/**
 * Сдвинуть показанное время к оценке за dtMs: в норме секунда за секунду; отстали — догоняем
 * до 1.5×, убежали вперёд — замедляемся, но назад не идём. Большое расхождение — сразу к оценке.
 */
export function stepClock(shown, target, dtMs) {
    if (shown === null || shown === undefined || Math.abs(target - shown) > CLOCK_SNAP_SEC) return target
    const diff = target - shown
    if (diff <= 0) return shown
    const rate = Math.min(1.5, 1 + diff * 0.5)
    return Math.min(target, shown + (dtMs / 1000) * rate)
}

/** Завершённые игры серии для «Итогов»; без серии — сама игра, если закончилась */
export function endedGames(series, battle) {
    const list = (series || []).filter((g) => g.state === 'end')
    if (list.length) return list
    return battle?.state === 'end' ? [battle] : []
}

/**
 * Игра серии, на которую OBS переключается сам: идёт после текущей и уже дошла до драфта.
 * Пока следующая игра в init, в эфире остаются итоги предыдущей.
 */
export function followGame(series, currentId) {
    const list = series || []
    const idx = list.findIndex((g) => g.id === currentId)
    if (idx < 0) return null
    return list.slice(idx + 1).reverse().find((g) => g.state && !['init', 'end'].includes(g.state)) || null
}

/** Номер игры в серии (1, 2, 3) или null, если серии нет */
export function gameNumber(series, battleId) {
    const idx = (series || []).findIndex((g) => g.id === battleId)
    return idx >= 0 ? idx + 1 : null
}
